/**
 * Uygulama kabugu: sinyal tarayici.
 *
 * AKIS
 *   zaman dilimi sec -> onaylanmis AL/SAT listesi -> coin detayi
 *
 * Veri akisi tek yonludur: backend -> App state -> bilesenler. Arayuz
 * hicbir gostergeyi KENDISI hesaplamaz; ekranda gorunen her sinyal
 * backend'in Indicator Mirror + Strategy Engine ciktisidir.
 *
 * MOBIL ONCELIKLI: alt gezinme sabittir, liste dar ekranda kart olur.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "./api/client";
import type {
  AuthUser,
  Health,
  MarketOverview,
  Settings,
  SignalScan,
} from "./api/types";
import { AdminPanel } from "./components/AdminPanel";
import {
  AuthScreen,
  ForcedPasswordChangeScreen,
  MembershipBlockedScreen,
} from "./components/AuthScreen";
import { CoinSheet } from "./components/CoinSheet";
import {
  AccountSheet,
  FavoritesScreen,
  MobileMenuSheet,
  SettingsScreen,
} from "./components/Screens";
import { IntradayPanel } from "./components/IntradayPanel";
import { AnalysisScreen } from "./components/AnalysisScreen";
import { BrandWatermark } from "./components/BrandWatermark";
import { MarketModeBanner } from "./components/MarketModeBanner";
import { PendingScreen } from "./components/PendingScreen";
import { ScanFooter } from "./components/ScanFooter";
import { Sidebar } from "./components/Sidebar";
import { Ticker } from "./components/Ticker";
import { MarketSideBanners } from "./components/MarketSideBanners";
import { QuickMovePanel } from "./components/QuickMovePanel";
import { PullToRefresh } from "./components/PullToRefresh";
import {
  ScanErrors,
  ScanKpis,
  ScanStatus,
  ScanTitle,
  SignalList,
  TimeframeChips,
  type SideFilter,
} from "./components/SignalScreen";
import {
  FilterSelect,
  IconBarChart,
  IconMenu,
  IconBell,
  IconClock,
  IconTarget,
  IconRefresh,
  IconSearch,
  IconShield,
  IconStar,
  IconTheme,
  IconTrendUp,
  IconUser,
  SearchField,
} from "./components/Ui";
import {
  type PriceAlert,
  clearLegacyAlerts,
  fromServer,
  takeLegacyAlerts,
} from "./lib/alerts";
import { registerServiceWorker } from "./lib/push";
import { belongsToSelection } from "./lib/signal";

type Theme = "light" | "dark";
type Tab =
  | "scan"
  | "quickmove"
  | "analysis"
  | "favorites"
  | "settings"
  | "admin"
  | "pending"
  | "vadeli";
type Toast = { message: string; tone: "ok" | "warn" | "error" } | null;
/** Oturum akisinin durumu - bkz. App() icindeki auth kapisi. */
type AuthState = "checking" | "guest" | "blocked" | "ok";

const THEME_KEY = "csb.theme";
const TIMEFRAME_KEY = "csb.timeframe";
/** Sinyaller yalnizca yeni bar kapaninca degisir; backend onbellekli.
 *  Kisa tutulur (dusuk gecikme) - /api/signals ucuz yanit verir. */
const POLL_MS = 5_000;


/** Telefonda alt seritte duran sekmeler - geri kalani "Menü" sayfasindan
 *  acilir (bkz. MobileMenuSheet). Genis ekranda sol cubuk HEPSINI gosterir. */
// Sicrama kaldirildi (kullanici 13.10) - Favoriler alt seride geri.
/** ⚠ + U+FE0E ("metin olarak ciz"): tek basina ⚠ telefonlarda sari emoji
 *  olarak ciziliyor. Isaret kalir, diger yazilar gibi tek renk gorunur. */
const WARN = "\u26A0\uFE0E";

const MOBILE_PRIMARY_TABS: readonly Tab[] = ["scan", "quickmove", "analysis", "favorites"];

function storedTheme(): Theme {
  try {
    const value = localStorage.getItem(THEME_KEY);
    if (value === "light" || value === "dark") return value;
  } catch {
    /* ozel pencerede erisim reddedilebilir */
  }
  return "dark";
}

function storedTimeframe(): string {
  try {
    return localStorage.getItem(TIMEFRAME_KEY) ?? "15m";
  } catch {
    return "15m";
  }
}

function audioContextCtor(): typeof AudioContext | undefined {
  return (
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}

function playTone(context: AudioContext, frequency: number, startAt: number, duration: number): void {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.value = frequency;
  gain.gain.setValueAtTime(0.05, startAt);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + duration);
}

/** Kisa uyari sesi (genel yeni sinyal) - disaridan varlik yuklenmez. */
function beep(): void {
  try {
    const Ctor = audioContextCtor();
    if (!Ctor) return;
    const context = new Ctor();
    playTone(context, 880, context.currentTime, 0.25);
    setTimeout(() => void context.close(), 400);
  } catch {
    /* ses calinamazsa sessizce gec */
  }
}

/** Elimde-var isaretli bir sembol icin sinyal geldiginde calinir - genel
 *  `beep()`den KASITLI OLARAK farkli (iki kisa, daha tiz vurus): kullanici
 *  ekrana bakmiyorken bile "bu digerlerinden onemli" hissi versin diye. */
/**
 * Sistem bildirimi (bildirim cubugu) - yalnizca izin verilmisse.
 *
 * HATA YUTULUR: Android Chrome Notification kurucusunu desteklemez
 * ("Illegal constructor" firlatir; orada yalnizca service worker ile
 * gosterilebilir, bizde yok). Bu cagri sinyal listesini guncelleyen kodun
 * ortasindaydi: Android'de izin verilmisse, yeni sinyal geldigi anda liste
 * guncellenmeyi birakiyordu.
 */
function systemNotify(title: string, body: string): void {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  void (async () => {
    // Android ve "Ana Ekrana Ekle" ile kurulan iPhone uygulamasi bildirimi
    // YALNIZCA service worker uzerinden gosterir (bkz. public/sw.js).
    try {
      const registration = await registerServiceWorker();
      if (registration) {
        await registration.showNotification(title, {
          body,
          icon: "/icon-192.png?v=2",
          badge: "/badge-96.png",
        });
        return;
      }
    } catch {
      /* asagidaki yola dus */
    }
    try {
      new Notification(title, { body });
    } catch {
      /* bu tarayicida sayfa icinden sistem bildirimi desteklenmiyor */
    }
  })();
}

function alertBeep(): void {
  try {
    const Ctor = audioContextCtor();
    if (!Ctor) return;
    const context = new Ctor();
    const now = context.currentTime;
    playTone(context, 1318.5, now, 0.14);
    playTone(context, 1318.5, now + 0.18, 0.18);
    setTimeout(() => void context.close(), 550);
  } catch {
    /* ses calinamazsa sessizce gec */
  }
}

/**
 * Uygulamanin AS KISMI (sinyal tarayici) - yalnizca AKTIF uyelikli bir
 * oturum varken monte edilir (bkz. asagidaki App()). Boylece oturum
 * kontrol edilmeden hicbir /api/scan, /api/settings vb. istegi atilmaz.
 */
function MainApp({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  const [theme, setTheme] = useState<Theme>(storedTheme);
  const [tab, setTab] = useState<Tab>("scan");
  const [timeframe, setTimeframe] = useState<string>(storedTimeframe);
  const [side, setSide] = useState<SideFilter>("buy");
  const [query, setQuery] = useState("");
  // Kalite>=5 filtresi (kullanici karari, bkz. research/quality_score_probe.py):
  // 4H'te kalite>=5 sinyaller istatistiksel olarak anlamli POZITIF getiri
  // gosterdi (GA sifiri icermiyor), kalite<5 negatif/anlamsizdi. YALNIZCA
  // 4H + AL icin gecerlidir - 15M'de ayni desen YOK (tum kalite seviyeleri
  // negatif), SAT'a hic uygulanmaz (elde tutulan coin icin cikis sinyali,
  // kalitesi ne olursa olsun HER ZAMAN gorunmeli). Baslangic degeri o anki
  // zaman dilimine gore akillica secilir (4H acik, 15M kapali) ama SONRASINDA
  // kullanicinin sectigi deger KALICIDIR - zaman dilimi degistirmek onu
  // sessizce sifirlamaz (kullanicinin tercihini "unutmak" kafa karistirici).
  // Kullanici istegi (25.10): Tarama her acilista TUM kalitelerle acilsin; kaliteliye kendisi gecer.
  const [qualityFilterOn, setQualityFilterOn] = useState(false);
  const [symbolScope, setSymbolScope] = useState<"all" | "held">("all");
  const [overview, setOverview] = useState<MarketOverview | null>(null);

  const [settings, setSettings] = useState<Settings | null>(null);
  const [scan, setScan] = useState<SignalScan | null>(null);
  // Gecmis ekrani KENDI zaman dilimini secer - Tarama sekmesinin
  // secimiyle BAGIMSIZDIR (4h Tarama'ya bakarken 15m gecmisi
  // incelenebilsin, birbirini etkilemesin).
  // Kullanici: "genel gecmisten 15m silinsin, sadece 4h". AL -> SAT
  // tamamlaninca dongu buraya duser.
  // Onay Bekleyenler ekrani da KENDI zaman dilimini secer (15m / 4h ayri).
  const [pendingTimeframe, setPendingTimeframe] = useState<string>("4h");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [health, setHealth] = useState<Health | null>(null);

  const [selected, setSelected] = useState<string | null>(null);

  // Telefon bildirimine dokunulunca ilgili coin acilir: bildirim adresi
  // "/?coin=BTCUSDT&tf=15m" bicimindedir (bkz. backend state.notify_user).
  // Uygulama kapaliysa sayfa bu adresle acilir; aciksa service worker mesaj
  // gonderir (public/sw.js notificationclick).
  useEffect(() => {
    const openFrom = (search: string) => {
      const params = new URLSearchParams(search);
      const coin = params.get("coin");
      const tf = params.get("tf");
      // "Guc tekrari" bildirimi Hizli Hareket'i acar.
      if (params.get("tab") === "quickmove") {
        setTab("quickmove");
        return;
      }
      if (!coin && !tf) return;
      if (tf === "15m" || tf === "4h") setTimeframe(tf);
      setTab("scan");
      // Yukselis ozeti bildirimi yalnizca periyodu tasir: o periyodun
      // tarama listesi acilir.
      if (coin) setSelected(coin.toUpperCase());
    };
    if (window.location.search) {
      openFrom(window.location.search);
      window.history.replaceState(null, "", window.location.pathname);
    }
    void registerServiceWorker();
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | null;
      if (data?.type === "open-coin" && data.url) openFrom(new URL(data.url, window.location.origin).search);
    };
    navigator.serviceWorker?.addEventListener("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onMessage);
  }, []);
  const [accountOpen, setAccountOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  // Elimde-var isaretli bir semboldeki AL/SAT sinyali gorulmeden kaldi mi -
  // Favoriler sekmesindeki rozet icin. Sekme acilinca temizlenir.
  const [heldAlertPending, setHeldAlertPending] = useState(false);
  // Fiyat & Sinyal Alarmlari (bkz. lib/alerts.ts) - "Elimde Var"dan AYRI,
  // kullanicinin TEK bir sembol icin tanimladigi ozel kosullar.
  // Sunucudan, istegi atan uyeye ozel yuklenir (bkz. lib/alerts.ts).
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);

  const toastTimer = useRef<number | null>(null);
  const seenSignals = useRef<Set<string>>(new Set());
  /** Onceki yoklamada listede olan coinler - yeni katilan coinin eski sinyali duyurulmaz. */
  const knownSymbols = useRef<Set<string>>(new Set());
  // Bir alarmin SAT-sinyali kosulu icin, ayni sinyali TEKRAR TEKRAR
  // bildirmemek adina hangi (sembol, zaman dilimi, sinyal_kapanis_zamani)
  // uclulerinin zaten bildirildigi.
  const seenAlertSignals = useRef<Set<string>>(new Set());
  // `favorites` burada da (state disinda) tutulur ki `loadSignals` her
  // taramada GUNCEL listeyi gorsun - `settings` degismedikce `loadSignals`
  // yeniden OLUSTURULMEZ (bkz. asagidaki senkronizasyon efekti).
  const notifyRef = useRef({ enabled: true, sound: false, favorites: [] as string[] });
  // Ekranda secili zaman dilimi: gec donen eski istekler buna gore elenir.
  const selectedFrame = useRef(timeframe);

  const notify = useCallback((message: string, tone: "ok" | "warn" | "error") => {
    setToast({ message, tone });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 5000);
  }, []);

  // KRITIK: bu ucu CoinSheet'e SABIT kimlikle gecmek zorundayiz. Satir ici
  // fonksiyon olsaydi App'in her arka plan yoklamasinda (POLL_MS=5sn)
  // yeniden render olmasiyla YENI kimlik alirdi; CoinSheet'in detay-cekme
  // efekti bu ucu deps'te tuttugu icin her 5 saniyede bir yeniden
  // tetiklenir, bir istek yarisa girerse detay ekranini KAPATIRDI
  // (kullanicinin bildirdigi "5 saniyede atiyor" hatasi buydu).
  const closeSheet = useCallback(() => setSelected(null), []);
  const handleSheetError = useCallback(
    (message: string) => notify(message, "error"),
    [notify],
  );
  const handleFavoriteChange = useCallback(
    (next: string[]) =>
      setSettings((current) => (current ? { ...current, favorites: next } : current)),
    [],
  );

  const fail = useCallback(
    (error: unknown, fallback: string) => {
      notify(error instanceof ApiError ? error.message : fallback, "error");
    },
    [notify],
  );

  /* ------------------------------------------------------------- tema */
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* yok sayilir */
    }
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(TIMEFRAME_KEY, timeframe);
    } catch {
      /* yok sayilir */
    }
  }, [timeframe]);

  const reloadAlerts = useCallback(async () => {
    try {
      setAlerts((await api.alerts()).map(fromServer));
    } catch {
      /* alarm listesi yardimci bilgi - hata ana ekrani bozmamali */
    }
  }, []);

  // Ilk yukleme + bir kerelik tasima: bu tarayicida eskiden localStorage'da
  // kalmis alarmlar varsa, simdi giris yapmis uyenin hesabina aktarilir ve
  // tarayicidan silinir (aksi halde ayni cihazdaki baska uye de gorurdu).
  useEffect(() => {
    void (async () => {
      const legacy = takeLegacyAlerts();
      if (legacy.length > 0) {
        for (const alert of legacy) {
          try {
            await api.createAlert({
              symbol: alert.symbol,
              sellTimeframes: alert.sellTimeframes ?? [],
              priceTarget: alert.priceTarget ?? null,
            });
          } catch {
            /* gecersiz eski kayit - atlanir */
          }
        }
        clearLegacyAlerts();
      }
      await reloadAlerts();
    })();
  }, [reloadAlerts]);

  /* -------------------------------------------------------- ilk yukleme */
  useEffect(() => {
    void (async () => {
      try {
        const payload = await api.settings();
        setSettings(payload);
        notifyRef.current = {
          enabled: payload.notifications.new_signals,
          sound: payload.notifications.sound,
          favorites: payload.favorites,
        };
      } catch (error) {
        fail(error, "Ayarlar alınamadı");
      }
      try {
        setHealth(await api.health());
      } catch {
        /* saglik bilgisi olmadan da arayuz acilir */
      }
    })();
  }, [fail]);

  /* ---------------------------------------------------------- sinyaller */
  const loadSignals = useCallback(
    async (
      frame: string,
      options: { refresh?: boolean; quiet?: boolean; notifyOnSuccess?: boolean } = {},
    ) => {
      if (!options.quiet) setLoading(true);
      try {
        const payload = await api.signals(frame, { refresh: options.refresh });
        // 15M istegi 4H secildikten sonra donerse 4H ekranina YAZILMAZ.
        if (!belongsToSelection(frame, selectedFrame.current, payload.timeframe)) return;

        // Yeni sinyal bildirimi: daha once GORULMEMIS ve TAZE olanlar.
        const fresh = payload.rows.filter(
          (row) => !seenSignals.current.has(`${row.symbol}:${row.signal_close_time}`),
        );
        const first = seenSignals.current.size === 0;
        for (const row of payload.rows) {
          seenSignals.current.add(`${row.symbol}:${row.signal_close_time}`);
        }
        // Takip listesi degisken (sunucu yalnizca Hizli Hareket/favori coinlerini
        // izler): listeye SONRADAN katilan coinin birkac mum onceki sinyali yeni
        // DEGILDIR (11.10: surekli bildirim).
        // (Mum kapanislari WS ile saniyelere yayilir; bu yuzden "son mum" yerine
        // "coin onceki yoklamada listede miydi?" bakilir.)
        const known = knownSymbols.current;
        knownSymbols.current = new Set(payload.rows.map((row) => row.symbol));
        // Yalnizca Yukselis: kullanici SAT'i yalnizca elindekinden cikmak icin kullanir;
        // Elimde Var/alarm coinlerinin SAT bildirimi sunucudan ayrica gelir (12.10).
        const announce = fresh.filter(
          (row) => row.age_bars <= 2 && known.has(row.symbol) && row.side === "BUY",
        );
        // NOT: elimde-var/alarm bildirimi burada DEGIL - sunucu tarafindaki
        // `check_alerts` + audit-log izleme dongusunde (bkz. asagida) -
        // boylece SEKME KAPALI/farkli zaman diliminde bile calisir. Burasi
        // yalnizca genel "N yeni sinyal" bilgisidir.
        if (!first && announce.length > 0 && notifyRef.current.enabled) {
          const names = announce.slice(0, 3).map((row) => `${row.symbol} ${row.side_label}`);
          notify(
            `${announce.length} yeni sinyal: ${names.join(", ")}${
              announce.length > 3 ? "…" : ""
            }`,
            "ok",
          );
          if (notifyRef.current.sound) beep();
          systemNotify("Sinyalix", names.join(", "));
        }

        setScan(payload);
        if (options.notifyOnSuccess) {
          notify(`Tarama güncellendi · ${payload.rows.length} sinyal`, "ok");
        }
      } catch (error) {
        if (!options.quiet && frame === selectedFrame.current) fail(error, "Sinyaller alınamadı");
      } finally {
        if (!options.quiet && frame === selectedFrame.current) setLoading(false);
      }
    },
    [fail, notify],
  );

  useEffect(() => {
    selectedFrame.current = timeframe;
    seenSignals.current = new Set();
    knownSymbols.current = new Set();
    setScan(null);
    void loadSignals(timeframe);
  }, [timeframe, loadSignals]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void loadSignals(timeframe, { quiet: true });
      }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [timeframe, loadSignals]);

  /** Satirdaki yildiz - "elimde var" isareti (SAT sinyalini cikis olarak
   *  vurgular). Mevcut `POST /api/favorites/{symbol}` ucunu kullanir. */
  const toggleFavorite = useCallback(
    async (symbol: string) => {
      try {
        const payload = await api.toggleFavorite(symbol);
        setSettings((current) =>
          current ? { ...current, favorites: payload.favorites } : current,
        );
      } catch (error) {
        fail(error, "Favori güncellenemedi");
      }
    },
    [fail],
  );

  // Satirlara SABIT bir fonksiyon gecer: her cizimde yeni bir ok-fonksiyonu
  // gecmek, degismeyen satirlari atlayan memo'yu (SignalScreen) bozuyordu.
  const toggleFavoriteQuiet = useCallback(
    (symbol: string) => void toggleFavorite(symbol),
    [toggleFavorite],
  );

  /* --------------------------------------------------- fiyat & sinyal alarmi */
  const addAlert = useCallback(
    (input: { symbol: string; sellTimeframes: string[]; priceTarget: number | null }) => {
      void (async () => {
        if (input.priceTarget != null) {
          try {
            // Sembol dogrulamasi: fiyati alinamayan bir sembole fiyat
            // hedefi kurulmasin. Yon (ustunde/altinda) sunucuda, mevcut
            // fiyata gore otomatik belirlenir.
            await api.signalDetail(input.symbol, input.sellTimeframes[0] ?? "15m");
          } catch (error) {
            fail(error, `${input.symbol}: fiyat alınamadı, alarm eklenemedi`);
            return;
          }
        }
        try {
          const created = await api.createAlert(input);
          setAlerts((current) => [...current, fromServer(created)]);
          notify(`${input.symbol} için alarm eklendi`, "ok");
        } catch (error) {
          fail(error, `${input.symbol}: alarm eklenemedi`);
        }
      })();
    },
    [fail, notify],
  );

  const removeAlert = useCallback(
    (id: string) => {
      void (async () => {
        try {
          await api.removeAlert(id);
          setAlerts((current) => current.filter((alert) => alert.id !== id));
        } catch (error) {
          fail(error, "Alarm kaldırılamadı");
        }
      })();
    },
    [fail],
  );

  // Alarmlari kontrol eden dongu - HANGI sekme/zaman dilimi acik olursa
  // olsun, bagimsiz calisir (App.tsx'teki `timeframe` secimine baglı DEGIL) -
  // aksi halde 4h alarmi 15m sekmesindeyken hic kontrol edilmezdi.
  useEffect(() => {
    if (alerts.length === 0) return;
    let cancelled = false;

    const check = async () => {
      for (const alert of alerts) {
        if (cancelled) return;
        let currentPrice: number | null = null;

        for (const tf of alert.sellTimeframes) {
          try {
            const detail = await api.signalDetail(alert.symbol, tf);
            currentPrice = detail.price;
            const active = detail.active;
            if (active == null || active.side !== "SELL") continue;
            const key = `${alert.symbol}:${tf}:${active.signal_close_time}`;
            if (seenAlertSignals.current.has(key)) continue;
            seenAlertSignals.current.add(key);
            notify(`${WARN} ${alert.symbol} (${tf.toUpperCase()}) için Düşüş sinyali geldi!`, "warn");
            if (notifyRef.current.sound) alertBeep();
            systemNotify("Sinyalix — Alarm", `${alert.symbol} ${tf.toUpperCase()} Düşüş sinyali geldi`);
          } catch {
            /* tek bir alarmin hatasi digerlerini durdurmamali */
          }
        }

        if (alert.priceTarget != null && alert.priceDirection != null) {
          if (currentPrice == null) {
            try {
              const detail = await api.signalDetail(alert.symbol, "15m");
              currentPrice = detail.price;
            } catch {
              continue;
            }
          }
          const reached =
            alert.priceDirection === "above"
              ? currentPrice >= alert.priceTarget
              : currentPrice <= alert.priceTarget;
          // Sunucu da ayni hedefi arka planda temizler (bkz. backend
          // check_alerts); o gelene kadar ayni hedef tekrar bildirilmesin.
          const priceKey = `${alert.id}:price:${alert.priceTarget}`;
          if (reached && !seenAlertSignals.current.has(priceKey)) {
            seenAlertSignals.current.add(priceKey);
            notify(
              `${WARN} ${alert.symbol} fiyatı hedefe ulaştı: ${currentPrice} (hedef ${alert.priceTarget})`,
              "warn",
            );
            if (notifyRef.current.sound) alertBeep();
            systemNotify("Sinyalix — Alarm", `${alert.symbol} fiyatı ${alert.priceTarget} hedefine ulaştı`);
            // Fiyat hedefi TEK SEFERLIKTIR: sunucudaki kayit guncellenir
            // (hedef silinir; Dusus-sinyali kosulu yoksa alarmin tamami).
            // Listeyi sunucudan tazelemek iki tarafi ayni tutar.
            void (async () => {
              try {
                if (alert.sellTimeframes.length === 0) {
                  await api.removeAlert(alert.id);
                }
              } catch {
                /* sunucu zaten temizlemis olabilir (404) */
              }
              await reloadAlerts();
            })();
          }
        }
      }
    };

    void check();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void check();
    }, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [alerts, notify, reloadAlerts]);

  /* ----------------------------------------------------------- ayarlar */
  const patchSettings = useCallback(
    async (body: Record<string, unknown>) => {
      setBusy(true);
      try {
        const payload = await api.updateSettings(body);
        setSettings(payload);
        notifyRef.current = {
          enabled: payload.notifications.new_signals,
          sound: payload.notifications.sound,
          favorites: payload.favorites,
        };
        if ("universe_limit" in body) {
          await loadSignals(timeframe, { refresh: true });
        }
      } catch (error) {
        fail(error, "Ayar güncellenemedi");
      } finally {
        setBusy(false);
      }
    },
    [fail, loadSignals, timeframe],
  );

  const toggleNotifications = useCallback(() => {
    const next = !(settings?.notifications.new_signals ?? true);
    if (next && typeof Notification !== "undefined" && Notification.permission === "default") {
      void Notification.requestPermission();
    }
    void patchSettings({ notify_new_signals: next });
  }, [settings, patchSettings]);

  /* ------------------------------------------------------------ turev */
  const timeframes = settings?.signals.available_timeframes ?? ["15m", "4h"];
  const favorites = settings?.favorites ?? [];

  // `notifyRef.current.favorites`i GUNCEL tutar - yildizla (`toggleFavorite`)
  // veya detay ekranindan (`handleFavoriteChange`) eklenen/cikan semboller
  // de dahil, favoriler HANGI yoldan degisirse degissin.
  useEffect(() => {
    notifyRef.current.favorites = favorites;
  }, [favorites]);

  // Favoriler sekmesine girilince bekleyen rozet temizlenir.
  useEffect(() => {
    if (tab === "favorites") setHeldAlertPending(false);
  }, [tab]);
  const rows = scan?.rows ?? [];

  // Ust serit + one cikanlar - bellekteki evrenden, hafif; 60 sn'de bir.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const payload = await api.marketOverview();
        if (!cancelled) setOverview(payload);
      } catch {
        /* yardimci serit - hata sessizce yok sayilir */
      }
    };
    void load();
    const id = window.setInterval(() => void load(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const visible = useMemo(() => {
    const wanted = side === "buy" ? "BUY" : "SELL";
    const needle = query.trim().toUpperCase();
    return rows.filter((row) => {
      if (side !== "all" && row.side !== wanted) return false;
      if (needle && !row.symbol.includes(needle)) return false;
      if (symbolScope === "held" && !favorites.includes(row.symbol)) return false;
      if (qualityFilterOn && row.side === "BUY" && (row.quality_score ?? 0) < 5) return false;
      return true;
    });
  }, [rows, side, query, qualityFilterOn, symbolScope, favorites]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      buy: rows.filter((row) => row.side === "BUY").length,
      sell: rows.filter((row) => row.side === "SELL").length,
    }),
    [rows],
  );

  const qualityBuyCount = useMemo(
    () => rows.filter((row) => row.side === "BUY" && (row.quality_score ?? 0) >= 5).length,
    [rows],
  );

  /** Spot-only koruma acik ve guvenli mod kapaliysa "tum sistemler aktif". */
  const systemsOk = Boolean(health?.spot_only) && !health?.safe_mode;

  useEffect(() => {
    const fallback = timeframes[0];
    if (fallback && !timeframes.includes(timeframe)) {
      setTimeframe(fallback);
    }
  }, [timeframes, timeframe]);

  // `category`, CSS'teki --cat-* degiskenlerine karsilik gelir (bkz.
  // theme.css .nav-item[data-category]) - her sekme kendi kimlik
  // rengini alsin diye (kullanici istegi: "her sey ayni renk, cok sade").
  const tabs: { key: Tab; label: string; icon: JSX.Element; category?: string }[] = [
    { key: "scan", label: "Tarama", icon: <IconSearch /> },
    { key: "quickmove", label: "Hızlı Hareket", icon: <IconTrendUp />, category: "quickmove" },
    { key: "analysis", label: "Analiz", icon: <IconBarChart />, category: "analysis" },
    { key: "favorites", label: "Favoriler", icon: <IconStar />, category: "favorites" },
    { key: "settings", label: "Duyurular", icon: <IconBell />, category: "announcements" },
    ...(user.is_admin
      ? [
          { key: "admin" as Tab, label: "Yönetim", icon: <IconShield />, category: "admin" },
          // Kullanici istegi: "yonetimin hemen altinda olsun". Yalnizca
          // yonetici gorur; onaylanmamis adaylar siradan uyeye sinyal
          // gibi gorunmemeli.
          {
            key: "pending" as Tab,
            label: "Onay Bekleyenler",
            icon: <IconClock />,
            category: "pending",
          },
          // Kullanici istegi (25.10): "onay bekleyenlerin altinda, ana sayfada". Yalniz
          // yonetici; bilgi amacli gun ici LONG firsatlari (bkz. IntradayPanel, CLAUDE.md Kural 1).
          { key: "vadeli" as Tab, label: "Vadeli", icon: <IconTarget /> },
        ]
      : []),
  ];

  /* Telefonda 8 sekme tek siraya sigmiyordu (etiketler kesiliyor, dokunma
     hedefi daraliyor). Referans tasarimdaki gibi 4 ana sekme + "Menü"
     gosterilir; geri kalanlar Menü sayfasindan acilir. GENIS EKRAN
     ETKILENMEZ - sol kenar cubugu HEPSINI gostermeye devam eder. */
  // Siralama MOBILE_PRIMARY_TABS'a gore (tabs dizisinin sirasina gore DEGIL):
  const primaryTabs = MOBILE_PRIMARY_TABS.flatMap((key) =>
    tabs.filter((item) => item.key === key),
  );
  const overflowTabs = tabs.filter((item) => !MOBILE_PRIMARY_TABS.includes(item.key));
  const overflowActive = overflowTabs.some((item) => item.key === tab);

  // Kullanici (08.10): hangi sayfada olursa olsun logoya basinca ana sayfa
  // (Tarama) - acik detay / menu / hesap paneli kapanir, sayfa basa doner.
  const goHome = () => {
    setTab("scan");
    setSelected(null);
    setMenuOpen(false);
    setAccountOpen(false);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="app" data-tab={tab}>
      <PullToRefresh />
      <Ticker overview={overview} />
      <MarketSideBanners />
      <Sidebar
        tabs={tabs}
        tab={tab}
        onTabChange={(key) => setTab(key as Tab)}
        theme={theme}
        heldAlertPending={heldAlertPending}
        systemsOk={systemsOk}
        version={health?.version}
        onSettings={() => setAccountOpen(true)}
        onHome={goHome}
      />
      <header className="header">
        <div className="header-inner">
          <button type="button" className="brand brand-home" onClick={goHome} aria-label="Ana sayfa (Tarama)">
            <BrandWatermark theme={theme} />
          </button>
          <nav className="nav" aria-label="Ana gezinme">
            {primaryTabs.map((item) => (
              <button
                key={item.key}
                type="button"
                className="nav-item"
                data-category={item.category}
                aria-current={tab === item.key ? "page" : undefined}
                onClick={() => setTab(item.key)}
              >
                <span className="nav-icon-wrap">
                  {item.icon}
                  {item.key === "favorites" && heldAlertPending ? (
                    <span className="nav-dot" aria-hidden="true" />
                  ) : null}
                </span>
                <span>{item.label}</span>
              </button>
            ))}
            <button
              type="button"
              className="nav-item"
              aria-current={overflowActive ? "page" : undefined}
              aria-haspopup="dialog"
              onClick={() => setMenuOpen(true)}
            >
              <span className="nav-icon-wrap">
                <IconMenu />
                {/* Favoriler artik Menu'de: "elimdeki coin'e Dusus sinyali
                    geldi" noktasi gozden kaybolmasin diye burada da. */}
                {heldAlertPending && !MOBILE_PRIMARY_TABS.includes("favorites") ? (
                  <span className="nav-dot" aria-hidden="true" />
                ) : null}
              </span>
              <span>Menü</span>
            </button>
          </nav>

          <div className="header-actions">
            <button
              type="button"
              className="icon-btn"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label={theme === "dark" ? "Açık temaya geç" : "Karanlık temaya geç"}
              data-active={theme === "dark" ? "true" : "false"}
            >
              <IconTheme />
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={toggleNotifications}
              aria-label="Bildirimler"
              data-active={settings?.notifications.new_signals ? "true" : "false"}
            >
              <IconBell />
              {settings?.notifications.new_signals ? <span className="dot" /> : null}
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={() => setAccountOpen(true)}
              aria-label="Hesap ve koruma"
              data-active={health?.safe_mode ? "true" : "false"}
            >
              <IconUser />
            </button>
          </div>
        </div>
      </header>

      <main className="app-main">
        {health && !health.spot_only ? (
          <div className="warn-banner error-banner">
            Spot-only koruması doğrulanamadı — işlem yapmayın.
          </div>
        ) : null}

        {tab === "scan" ? (
          <div className="scan-screen">
            <div className="page-head scan-head">
              <div className="page-head-text">
                <ScanTitle timeframe={timeframe} />
                <ScanStatus scan={scan} loading={loading} />
              </div>
              {/* Hizli Hareket zaten sol cubukta kendi sekmesinde;
                  burasi artik "piyasa su an ne agirlikta" sorusuna ayrildi. */}
              <MarketModeBanner scan={scan} />
            </div>
            <ScanKpis scan={scan} rows={rows} counts={counts} side={side} onSideChange={setSide} />
            <div className="toolbar scan-toolbar">
              <TimeframeChips timeframes={timeframes} value={timeframe} onChange={setTimeframe} />
              <SearchField value={query} onChange={setQuery} placeholder="Sembol ara (örn. BTC, ETH, SOL...)" />
              <FilterSelect
                label="Sembol kapsamı"
                value={symbolScope}
                onChange={(next) => setSymbolScope(next === "held" ? "held" : "all")}
                options={[
                  { value: "all", label: "Tüm Semboller" },
                  { value: "held", label: `Elimdekiler (${favorites.length})` },
                ]}
              />
              <FilterSelect
                label="Kalite filtresi"
                value={qualityFilterOn ? "5" : "all"}
                onChange={(next) => setQualityFilterOn(next === "5")}
                disabled={side === "sell"}
                title="Yalnızca Yükseliş sinyallerine uygulanır; Düşüş (çıkış) sinyalleri her zaman görünür. Kalite ≥5 4H'te araştırmayla doğrulandı."
                options={[
                  { value: "all", label: "Tüm Kaliteler" },
                  { value: "5", label: `Kaliteli Yükseliş (5+) · ${qualityBuyCount}` },
                ]}
              />
              <button
                type="button"
                className="icon-btn"
                onClick={() => void loadSignals(timeframe, { refresh: true, notifyOnSuccess: true })}
                aria-label="Yenile"
                disabled={loading}
              >
                <IconRefresh spinning={loading} />
              </button>
            </div>
            <ScanErrors scan={scan} />
            <SignalList
              rows={visible}
              timeframe={timeframe}
              loading={loading}
              onSelect={setSelected}
              holdings={favorites}
              onToggleFavorite={toggleFavoriteQuiet}
              emptyTitle={query.trim() ? `"${query.trim()}" ile eşleşen sinyal yok` : "Onaylanmış sinyal yok"}
              emptyDetail={
                scan
                  ? `${scan.scanned} sembol tarandı. Kural: ${scan.rule_tr}.`
                  : "Tarama bekleniyor."
              }
            />
            <ScanFooter
              scan={scan}
              overview={overview}
              startedAt={health?.started_at ?? null}
              pollMs={POLL_MS}
              onSelect={setSelected}
            />
          </div>
        ) : null}

        {tab === "quickmove" ? (
          <div data-category="quickmove">
            <QuickMovePanel onSelect={setSelected} />
          </div>
        ) : null}

        {tab === "analysis" ? (
          <div data-category="analysis">
            <AnalysisScreen theme={theme} onSelect={setSelected} onError={handleSheetError} />
          </div>
        ) : null}

        {tab === "favorites" ? (
          <div data-category="favorites">
            <FavoritesScreen
              favorites={favorites}
              rows={rows}
              loading={loading}
              timeframe={timeframe}
              onSelect={setSelected}
            />
          </div>
        ) : null}

        {tab === "settings" ? (
          <div data-category="announcements">
            <SettingsScreen settings={settings} />
          </div>
        ) : null}

        {tab === "admin" && user.is_admin ? (
          <div data-category="admin">
            <AdminPanel notify={notify} />
          </div>
        ) : null}

        {tab === "vadeli" && user.is_admin ? (
          <div>
            <h2 className="section-title">Vadeli · Gün içi LONG</h2>
            <IntradayPanel onError={(message) => notify(message, "error")} />
          </div>
        ) : null}

        {tab === "pending" && user.is_admin ? (
          <div data-category="pending">
            <PendingScreen
              timeframe={pendingTimeframe}
              timeframes={timeframes}
              onTimeframeChange={setPendingTimeframe}
              onSelect={setSelected}
            />
          </div>
        ) : null}
      </main>

      <footer className="footer">
        <span className="footer-brand">
          <strong>Sinyalix</strong>
          {health ? <span className="footer-version">v{health.version}</span> : null}
          <span className="footer-sep">|</span>
          <span>Daha akıllı işlemler, daha özgür yarınlar.</span>
        </span>
        <span className="footer-status" data-state={systemsOk ? "ok" : "warn"}>
          <span className="pulse" />
          {systemsOk ? "Tüm sistemler aktif" : "Sistem kontrolü gerekiyor"}
        </span>
      </footer>

      {selected ? (
        <CoinSheet
          symbol={selected}
          timeframe={timeframe}
          theme={theme}
          onClose={closeSheet}
          onFavoriteChange={handleFavoriteChange}
          onError={handleSheetError}
        />
      ) : null}

      {accountOpen ? (
        <AccountSheet
          user={user}
          settings={settings}
          busy={busy}
          alerts={alerts}
          onPatch={(body) => void patchSettings(body)}
          onAddAlert={addAlert}
          onRemoveAlert={removeAlert}
          onClose={() => setAccountOpen(false)}
          onLogout={onLogout}
        />
      ) : null}

      {menuOpen ? (
        <MobileMenuSheet
          items={overflowTabs}
          dotFor={heldAlertPending ? "favorites" : null}
          activeTab={tab}
          onSelect={(key) => setTab(key as Tab)}
          onClose={() => setMenuOpen(false)}
          onAccount={() => setAccountOpen(true)}
          theme={theme}
          onThemeToggle={() => setTheme(theme === "dark" ? "light" : "dark")}
        />
      ) : null}

      {toast ? (
        <div className="toast" data-tone={toast.tone} role="status">
          {toast.message}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Oturum kapisi.
 *
 * `/api/auth/me` HER ZAMAN cagrilir (halka acik uc, bkz. auth_deps.py
 * PUBLIC_API_PREFIXES) - basarili yanit oturumun GECERLI oldugunu
 * gosterir, govdedeki `status` de uyeligin ACTIVE olup olmadigini
 * (CANLI hesaplanir, bkz. backend User.effective_status). `MainApp`
 * yalnizca status ACTIVE iken monte edilir; boylece oturum
 * dogrulanmadan hicbir sinyal/tarama istegi atilmaz.
 */
export function App() {
  const [authState, setAuthState] = useState<AuthState>("checking");
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authBusy, setAuthBusy] = useState(false);

  const applyUser = useCallback((user: AuthUser) => {
    setAuthUser(user);
    setAuthState(user.status === "ACTIVE" ? "ok" : "blocked");
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        applyUser(await api.auth.me());
      } catch {
        setAuthState("guest");
      }
    })();
  }, [applyUser]);

  const logout = useCallback(() => {
    setAuthBusy(true);
    void (async () => {
      try {
        await api.auth.logout();
      } catch {
        /* cerez zaten gecersizse de yerel oturum temizlenir */
      } finally {
        setAuthUser(null);
        setAuthState("guest");
        setAuthBusy(false);
      }
    })();
  }, []);

  if (authState === "checking") {
    return <div className="auth-screen" aria-busy="true" />;
  }
  if (authState === "guest") {
    return <AuthScreen onAuthenticated={applyUser} />;
  }
  if (authState === "blocked" && authUser) {
    return <MembershipBlockedScreen user={authUser} onLogout={logout} busy={authBusy} />;
  }
  // Uyelik kontrolunden SONRA gelir: suresi dolmus bir uyeye once
  // "uyeliginiz bitti" denmeli, sifre belirletmek anlamsiz olurdu.
  if (authUser?.must_change_password) {
    return (
      <ForcedPasswordChangeScreen
        user={authUser}
        onChanged={applyUser}
        onLogout={logout}
        busy={authBusy}
      />
    );
  }
  return <MainApp user={authUser as AuthUser} onLogout={logout} />;
}
